import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { publicAPI } from '../services/api';
import ReCAPTCHA from 'react-google-recaptcha';
import INDIA_STATES_DISTRICTS from '../data/indiaStatesDistricts';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI';

const AREAS_OF_INTEREST = [
  { value: 'plantation', label: 'Plantation' },
  { value: 'biodiversity_promotion', label: 'Biodiversity Promotion' },
  { value: 'soil_restoration', label: 'Soil Restoration' },
  { value: 'carbon_sequestration', label: 'Carbon Sequestration' },
];

export default function RegisterPage() {
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get('ref') || '';
  const [referrerName, setReferrerName] = useState('');
  const [role, setRole] = useState('individual');
  const [formData, setFormData] = useState({
    name: '', email: '', password: '', phone: '',
    address: '', city: '', state: '', pincode: '',
    organisation_name: '', registration_number: '', csr_policy_reference: '',
    website: '', contact_person: '', designation: '',
    past_work_portfolio: '', areas_of_operation: '', capacity_details: '',
    years_of_experience: '', id_type: '', id_number: '',
  });
  const [areasOfInterest, setAreasOfInterest] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const captchaRef = useRef(null);
  const { register } = useAuth();

  useEffect(() => {
    if (!refCode) return;
    publicAPI.validateReferral(refCode)
      .then(res => { if (res.data.valid) setReferrerName(res.data.referrer?.name || ''); })
      .catch(() => {});
  }, [refCode]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const toggleArea = (area) => {
    setAreasOfInterest(prev =>
      prev.includes(area) ? prev.filter(a => a !== area) : [...prev, area]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!captchaToken) {
      setError('Please complete the CAPTCHA verification');
      return;
    }

    setLoading(true);
    try {
      await register({
        ...formData,
        role,
        areas_of_interest: areasOfInterest,
        referral_code: refCode || undefined,
        captcha_token: captchaToken,
      });
      setSuccess('Registration submitted successfully! You will receive credentials after admin approval.');
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed');
      // Reset captcha on failure
      if (captchaRef.current) captchaRef.current.reset();
      setCaptchaToken(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card" style={{ maxWidth: 600 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginBottom: 14 }}>
          <img src={`${process.env.PUBLIC_URL || ''}/haritanjali_logo.png`} alt="Haritanjali"
            style={{ width: 78, height: 78, objectFit: 'contain' }} />
          <div style={{ textAlign: 'left' }}>
            <h1 style={{ fontSize: 28, marginBottom: 0, color: '#2d5016', fontWeight: 800, lineHeight: 1.1 }}>हरितांजलि</h1>
            <p style={{ fontSize: 11, color: '#2d5016', letterSpacing: 4, textTransform: 'uppercase', margin: '2px 0 0 0', fontWeight: 600 }}>HARITANJALI</p>
            <p style={{ fontSize: 12, color: '#E8751A', fontStyle: 'italic', margin: '4px 0 0 0', fontWeight: 500 }}>जहाँ पेड़, वहाँ जीवन</p>
          </div>
        </div>
        <p className="subtitle">Create your account</p>

        {refCode && (
          <div style={{
            background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10,
            padding: '12px 16px', marginBottom: 16, textAlign: 'center'
          }}>
            <span style={{ fontSize: 13, color: '#166534' }}>
              {referrerName
                ? <>Referred by <strong>{referrerName}</strong> &bull; Code: <strong style={{ letterSpacing: 1 }}>{refCode}</strong></>
                : <>Referral Code: <strong style={{ letterSpacing: 1 }}>{refCode}</strong></>
              }
            </span>
          </div>
        )}

        {error && <div className="error">{error}</div>}
        {success && <div className="success">{success}</div>}

        {!success && (
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Register As</label>
              <select value={role} onChange={(e) => setRole(e.target.value)}>
                <option value="individual">Individual</option>
                <option value="csr">CSR Entity</option>
                <option value="agency">Implementing Agency</option>
              </select>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>{role === 'individual' ? 'Full Name' : 'Contact Name'} *</label>
                <input name="name" value={formData.name} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input type="email" name="email" value={formData.email} onChange={handleChange} required />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Password *</label>
                <input type="password" name="password" value={formData.password} onChange={handleChange} required minLength={6} />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input name="phone" value={formData.phone} onChange={handleChange} />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>State</label>
                <select name="state" value={formData.state} onChange={(e) => {
                  setFormData({ ...formData, state: e.target.value, city: '' });
                }}>
                  <option value="">Select State</option>
                  {Object.keys(INDIA_STATES_DISTRICTS).map(st => (
                    <option key={st} value={st}>{st}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>District</label>
                <select name="city" value={formData.city} onChange={handleChange} disabled={!formData.state}>
                  <option value="">Select District</option>
                  {(INDIA_STATES_DISTRICTS[formData.state] || []).map(dist => (
                    <option key={dist} value={dist}>{dist}</option>
                  ))}
                </select>
              </div>
            </div>

            {role !== 'individual' && (
              <>
                <div className="form-group">
                  <label>Organisation Name *</label>
                  <input name="organisation_name" value={formData.organisation_name} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>Registration Number</label>
                  <input name="registration_number" value={formData.registration_number} onChange={handleChange} />
                </div>
              </>
            )}

            {role === 'csr' && (
              <div className="form-row">
                <div className="form-group">
                  <label>Website</label>
                  <input name="website" value={formData.website} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>CSR Policy Reference</label>
                  <input name="csr_policy_reference" value={formData.csr_policy_reference} onChange={handleChange} />
                </div>
              </div>
            )}

            {role === 'agency' && (
              <>
                <div className="form-group">
                  <label>Areas of Operation</label>
                  <textarea name="areas_of_operation" value={formData.areas_of_operation} onChange={handleChange} />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Capacity Details</label>
                    <input name="capacity_details" value={formData.capacity_details} onChange={handleChange} />
                  </div>
                  <div className="form-group">
                    <label>Years of Experience</label>
                    <input type="number" name="years_of_experience" value={formData.years_of_experience} onChange={handleChange} />
                  </div>
                </div>
              </>
            )}

            {role === 'individual' && (
              <div className="form-row">
                <div className="form-group">
                  <label>ID Type</label>
                  <select name="id_type" value={formData.id_type} onChange={handleChange}>
                    <option value="">Select</option>
                    <option value="aadhaar">Aadhaar</option>
                    <option value="pan">PAN</option>
                    <option value="passport">Passport</option>
                    <option value="voter_id">Voter ID</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>ID Number</label>
                  <input name="id_number" value={formData.id_number} onChange={handleChange} />
                </div>
              </div>
            )}

            <div className="form-group">
              <label>Areas of Interest</label>
              <div className="checkbox-group">
                {AREAS_OF_INTEREST.map(area => (
                  <label key={area.value}>
                    <input
                      type="checkbox"
                      checked={areasOfInterest.includes(area.value)}
                      onChange={() => toggleArea(area.value)}
                    />
                    {area.label}
                  </label>
                ))}
              </div>
            </div>

            {/* Google reCAPTCHA */}
            <div style={{ display: 'flex', justifyContent: 'center', margin: '16px 0' }}>
              <ReCAPTCHA
                ref={captchaRef}
                sitekey={RECAPTCHA_SITE_KEY}
                onChange={(token) => setCaptchaToken(token)}
                onExpired={() => setCaptchaToken(null)}
              />
            </div>

            <button type="submit" className="btn btn-primary btn-block" disabled={loading || !captchaToken}>
              {loading ? 'Submitting...' : 'Submit Registration'}
            </button>
          </form>
        )}

        <div className="auth-footer">
          Already have an account? <Link to="/login">Sign in</Link>
        </div>
      </div>
    </div>
  );
}
